/**
 * 自社予約サイト料金計算 API ラッパー
 *
 * Endpoint: GET https://n-car.jp/booking/calc/
 *
 * 基本料金・短時間レンタル料金・キャンペーン割引・NOC/CDW・オプション(チャイルド/ブースターシート)
 * を一括で計算し、車両タイプごとの在庫と料金内訳を返す。
 *
 * このサービスが料金計算のソース・オブ・トゥルース。
 * 旧 feePolicy.ts (固定単価 NOC=770/CDW=2200) は廃止。
 */

import { SITE_ORIGIN } from '../constants/calendar';
import { getStoreKey, getSiteOrigin } from '../config/backend';
import { debug } from '../lib/debug';

export interface BookingCalcParams {
  /** 貸出日時 ISO 8601 (YYYY-MM-DDTHH:mm) */
  start: string;
  /** 返却日時 ISO 8601 (YYYY-MM-DDTHH:mm) */
  end: string;
  /** 言語 (デフォルト 'ja') */
  lang?: 'ja' | 'en' | 'kr' | 'zh' | string;
  noc?: boolean;
  cdw?: boolean;
  child_seat?: number;
  booster_seat?: number;
  /** 車両タイプコードで絞り込みたい場合 */
  car_type?: string;
  /**
   * 'cruise' = クルーズ船の予約（宮古=平良港・那覇=那覇港）。クルーズ船料金表を使い、送迎料（宮古 ¥4,000・那覇 ¥5,000）を
   * breakdown.location_surcharge に入れる（クルーズ料金を1日でも使えば 0）。正: booking-platform/md/pricing.md
   */
  location?: 'cruise';
}

export interface BookingCalcBreakdown {
  first_night: number;
  extra_nights: number;
  short: number;
  subtotal_base: number;
  discount: number;
  coupon_applied: boolean;
  fees: number;
  options: number;
  insurance: number;
  /** insurance の内訳（2026-10-09〜。古い Calc API には無い） */
  noc?: number;
  cdw?: number;
  /** 連休パック価格（CDW/NOC込み・pack_days 分）。パック時は first_night=0 でここに入る */
  holiday_pack?: number;
  /** 平良港の送迎料（location=cruise のときだけ。クルーズ料金を使えば 0。grand_total に含む） */
  location_surcharge?: number;
  grand_total: number;
}

/** 連休パック（期間にかかり、その車種にパック価格があるとき）。正: booking-platform/md/pricing.md「連休パック」 */
export interface BookingCalcHolidayPack {
  period_id: string;
  period_name: string;
  period_from: string;
  period_to: string;
  price: number;
  /** パックに含めた課金日数（最大 pack_days） */
  covered_days: number;
  /** 補償(NOC/CDW)を別途加算した日数（パック超過分・期間外分） */
  coverage_days: number;
}

/** クルーズ船料金表を使ったとき（location=cruise）。days = クルーズ料金で計算した課金日数 */
export interface BookingCalcCruisePricing {
  days: number;
}

export interface BookingCalcVehicle {
  code: string;
  name: string;
  name_ja: string;
  base_price: number;
  first_night_price: number;
  total_stock: number;
  available_stock: number;
  is_request_reservation: boolean;
  campaign_eligible: boolean;
  etc_card_free: boolean;
  breakdown: BookingCalcBreakdown;
  /** 連休パック適用時のみ。未適用は null（古い Calc API では undefined） */
  holiday_pack?: BookingCalcHolidayPack | null;
  /** 'pickup' | 'return' | 'span' | 'no_pack' | null — 連休パック期間に関わるリクエスト理由 */
  pack_request_reason?: 'pickup' | 'return' | 'span' | 'no_pack' | null;
  /** location=cruise でクルーズ船料金表を使ったときだけ。未使用は null（古い Calc API では undefined） */
  cruise_pricing?: BookingCalcCruisePricing | null;
}

export interface BookingCalcCampaign {
  id: number;
  name: string;
  discount_percent: string | number | null;
  etc_free: boolean;
  eligible_car_codes: string[];
  rental_start_from: string;
  rental_end_to: string;
}

export interface BookingCalcResponse {
  ok: boolean;
  request: {
    start: string;
    end: string;
    lang: string;
    options: { noc: boolean; cdw: boolean; child_seat: number; booster_seat: number };
    car_type: string | null;
  };
  duration: { total_minutes: number; nights: number; short: boolean; option_days: number };
  campaign: BookingCalcCampaign | null;
  vehicles: BookingCalcVehicle[];
  /** 期間にかかる連休パック（無ければ null） */
  holiday_pack_period?: { id: string; name: string; from: string; to: string; pack_days: number } | null;
}

// 計算 API のホスト解決:
// - localhost: 空文字（setupProxy.js が /booking → n-car.jp に中継）
// - その他（本番含む）: 絶対 URL https://n-car.jp
//   ※ SITE_ORIGIN が空文字になると booking.trailernchu.co.jp/booking/calc/ に
//     fall-through して SPA の index.html が返り JSON parse でコケるため明示
const isLocalhost = (): boolean => {
  if (typeof window === 'undefined' || !window.location) return false;
  const h = window.location.hostname;
  return h === 'localhost' || h === '127.0.0.1';
};

/** calc API の店舗キー（r-cal の ncar/pit → calc の naha/miyako） */
export const calcStoreKey = (): 'naha' | 'miyako' => (getStoreKey() === 'pit' ? 'miyako' : 'naha');

const resolveCalcOrigin = (): string => {
  if (isLocalhost()) return ''; // 相対 URL → setupProxy.js
  // 宮古は宮古のドメイン（pit-rent.com/booking/calc はホストでも宮古と判定される）
  if (getStoreKey() === 'pit') return getSiteOrigin() || 'https://pit-rent.com';
  // SITE_ORIGIN が空でも production の場合は強制的に絶対 URL にする
  return SITE_ORIGIN || 'https://n-car.jp';
};

const buildUrl = (params: BookingCalcParams): string => {
  const search = new URLSearchParams();
  search.set('start', params.start);
  search.set('end', params.end);
  search.set('lang', params.lang || 'ja');
  // 店舗を明示する（2026-10-03〜 calc API は店舗対応。宮古 = backend-miyako・宮古の補償単価・深夜料金なし）
  search.set('store', calcStoreKey());
  if (params.noc !== undefined) search.set('noc', params.noc ? '1' : '0');
  if (params.cdw !== undefined) search.set('cdw', params.cdw ? '1' : '0');
  if (params.child_seat != null) search.set('child_seat', String(params.child_seat));
  if (params.booster_seat != null) search.set('booster_seat', String(params.booster_seat));
  if (params.car_type) search.set('car_type', params.car_type);
  // クルーズ船の予約（宮古=平良港・那覇=那覇港。那覇は 2026-10-31〜）
  if (params.location === 'cruise') search.set('location', 'cruise');
  return `${resolveCalcOrigin()}/booking/calc/?${search.toString()}`;
};

/** ISO 8601 整形: 「YYYY-MM-DD HH:mm」「YYYY-MM-DDTHH:mm:SS+09:00」など複数形式を吸収 */
export function toCalcDateTime(input: string | Date): string {
  if (input instanceof Date) {
    const y = input.getFullYear();
    const m = String(input.getMonth() + 1).padStart(2, '0');
    const d = String(input.getDate()).padStart(2, '0');
    const hh = String(input.getHours()).padStart(2, '0');
    const mi = String(input.getMinutes()).padStart(2, '0');
    return `${y}-${m}-${d}T${hh}:${mi}`;
  }
  const trimmed = String(input).trim();
  // タイムゾーン部分・秒は削除
  const noTz = trimmed.replace(/(\+|-)\d{2}:\d{2}$/, '').replace(/Z$/, '');
  const noSec = noTz.replace(/(:\d{2}):\d{2}$/, '$1');
  // 空白を T に正規化
  return noSec.replace(' ', 'T');
}

export async function fetchBookingCalc(params: BookingCalcParams): Promise<BookingCalcResponse> {
  const url = buildUrl(params);
  const hostname = typeof window !== 'undefined' ? window.location.hostname : '(no-window)';
  const origin = typeof window !== 'undefined' ? window.location.origin : '(no-window)';

  // 診断用に常時出力（debug フラグに依存しない）
  // eslint-disable-next-line no-console
  console.info('[bookingCalc] →', { url, hostname, origin, params });

  let res: Response;
  try {
    res = await fetch(url, { method: 'GET' });
  } catch (e: any) {
    // CORS / ネットワーク失敗はここに落ちる（ブラウザは詳細を伏せるが TypeError になる）
    // eslint-disable-next-line no-console
    console.error('[bookingCalc] fetch failed', { url, hostname, error: e });
    const corsHint = isLocalhost()
      ? ' [ヒント] localhost dev で /booking プロキシが効いていません。dev server コンソールに [proxy] /booking -> https://n-car.jp が出ているか確認し、setupProxy.js 変更後は npm start を完全に再起動してください。'
      : ' [ヒント] CORS によりブラウザが応答を拒否した可能性があります。DevTools の Network タブで /booking/calc/ のステータスを確認してください。';
    throw new Error(`booking calc API: fetch 自体が失敗 (${e?.message || e}) url=${url} hostname=${hostname}${corsHint}`);
  }

  // eslint-disable-next-line no-console
  console.info('[bookingCalc] ←', {
    url,
    status: res.status,
    contentType: res.headers.get('content-type'),
    finalUrl: res.url,
  });

  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(`booking calc API error: ${res.status} ${text.slice(0, 200)} (url=${url})`);
  }
  // content-type で HTML が混入していないか検証（fall-through で index.html が返る事故対策）
  const contentType = res.headers.get('content-type') || '';
  if (!contentType.includes('json')) {
    const preview = await res.text().catch(() => '');
    // eslint-disable-next-line no-console
    console.error('[bookingCalc] non-json response', {
      url,
      contentType,
      finalUrl: res.url,
      preview: preview.slice(0, 200),
    });
    const hint = isLocalhost()
      ? ' [ヒント] localhost dev で /booking プロキシが効いていない可能性があります。dev server コンソールに [proxy] /booking -> https://n-car.jp の表示があるか確認し、無ければ npm start を再起動してください。'
      : '';
    throw new Error(`booking calc API: 想定外の content-type "${contentType}" を受信しました (finalUrl=${res.url})${hint}`);
  }
  const json = (await res.json()) as BookingCalcResponse;
  if (!json.ok) {
    throw new Error('booking calc API returned ok=false');
  }
  // 店舗の取り違え防止: 宮古の画面で、宮古と名乗らない応答（店舗未対応の旧 API = 那覇の料金・在庫）は使わない。
  // calc API の店舗対応（new-n-car）が本番に出る前後どちらでも、那覇の料金で宮古の予約を計算しない。
  const want = calcStoreKey();
  const got = (json as any).store ?? 'naha';
  if (got !== want) {
    throw new Error(`booking calc API: 店舗が違う応答です（要求 ${want} / 応答 ${got}）。`
      + '料金計算 API が宮古に未対応の版のため、この画面では使えません');
  }
  debug.log('bookingCalcService', 'fetched', {
    nights: json.duration.nights,
    vehicles: json.vehicles.length,
    campaign: json.campaign?.name,
  });
  return json;
}
